import {
  getCoreAcademicNamespace,
  getPermissionUtils,
  getPersonalSettings,
  getUser,
} from '@tyro/api';
import { LoadingScreen } from '@tyro/core';
import { type ReactNode, useEffect, useState } from 'react';
import { Outlet, type RouteObject, redirect } from 'react-router';
import type { NavCategory } from '../@types/nav-object';
import { ErrorElement } from '../components/error-element';
import { buildPathsWithAccess } from '../utils/build-paths-with-access';
import { buildRouteTree } from '../utils/build-route-tree';
import { sentryCreateBrowserRouter, setSentryUser } from '../utils/sentry';

export interface AppRouterProps {
  navCategories: NavCategory[];
  Shell: React.FC<{ children: ReactNode }>;
  additionalRoutes: RouteObject[];
}

export function useAppRouter({
  navCategories,
  Shell,
  additionalRoutes,
}: AppRouterProps) {
  const [router, setRouter] = useState<ReturnType<
    typeof sentryCreateBrowserRouter
  > | null>(null);

  const createRouter = async () => {
    const fullPathsWithAccess = await buildPathsWithAccess(navCategories);
    const routeTree = buildRouteTree(navCategories, fullPathsWithAccess);

    setRouter(
      sentryCreateBrowserRouter([
        {
          loader: async () => {
            try {
              const responses = await Promise.all([
                getUser(),
                getCoreAcademicNamespace(),
              ]);
              const [user] = responses;
              setSentryUser(user.activeProfile);

              return responses;
            } catch (error) {
              if (
                error instanceof Error &&
                error?.message === 'Failed to fetch'
              ) {
                throw new Response('Service Unavailable', { status: 503 });
              }

              throw error;
            }
          },
          element: (
            <Shell>
              <Outlet />
            </Shell>
          ),
          errorElement: <ErrorElement Shell={Shell} />,
          HydrateFallback: LoadingScreen,
          children: [
            {
              path: '/',
              loader: async () => {
                const [
                  permissions,
                  // { activeProfile },
                ] = await Promise.all([
                  getPermissionUtils(),
                  getUser(),
                  getPersonalSettings(),
                ]);

                if (permissions.isStudent || permissions.isContact) {
                  return redirect('/people/students');
                }

                if (permissions.isTyroTenantAndUser) {
                  return redirect('/admin/schools');
                }

                // Add back in for ETB dashboard
                // if (
                //   activeProfile?.tenant.tenantType === TenantType.Organization
                // ) {
                //   return redirect('/substitution');
                // }

                return redirect('/dashboard');
              },
            },
            ...routeTree,
          ],
        },
        ...additionalRoutes,
        {
          path: '*',
          loader: () => {
            throw new Response('Not Found', { status: 404 });
          },
          errorElement: <ErrorElement Shell={Shell} />,
        },
      ]),
    );
  };

  useEffect(() => {
    createRouter();
  }, []);

  return router;
}
